import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const LeaveCalendar = ({ filters, dateRange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarData, setCalendarData] = useState({});

  // Mock leave data - in real app, this would come from API
  const leaveTypes = {
    vacation: { name: 'Vacation', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
    sick: { name: 'Sick Leave', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
    personal: { name: 'Personal', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
    holiday: { name: 'Company Holiday', color: 'bg-purple-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50' }
  };

  const mockLeaveData = {
    '2024-11-15': [
      { id: 1, employeeName: 'John Doe', type: 'vacation', status: 'approved' },
      { id: 2, employeeName: 'Sarah Wilson', type: 'sick', status: 'approved' }
    ],
    '2024-11-20': [
      { id: 3, employeeName: 'Mike Johnson', type: 'personal', status: 'pending' }
    ],
    '2024-11-25': [
      { id: 4, employeeName: 'Company Holiday', type: 'holiday', status: 'approved' }
    ]
  };

  useEffect(() => {
    // Simulate API call to fetch calendar data
    setCalendarData(mockLeaveData);
  }, [filters, dateRange]);

  const generateCalendarDays = () => {
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayWeek = firstDay?.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = firstDayWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days?.push({
        date: date,
        isCurrentMonth: false,
        dateString: date?.toISOString()?.split('T')?.[0]
      });
    }
    
    // Current month days
    for (let day = 1; day <= lastDay?.getDate(); day++) {
      const date = new Date(year, month, day);
      days?.push({
        date: date,
        isCurrentMonth: true,
        dateString: date?.toISOString()?.split('T')?.[0]
      });
    }
    
    // Next month days to fill the grid
    const remainingDays = 42 - days?.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days?.push({
        date: date,
        isCurrentMonth: false,
        dateString: date?.toISOString()?.split('T')?.[0]
      });
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate?.setMonth(prev?.getMonth() + direction);
      return newDate;
    });
  };

  const getLeaveForDate = (dateString) => {
    return calendarData?.[dateString] || [];
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const days = generateCalendarDays();

  return (
    <div className="p-6">
      {/* Calendar Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Icon name="ChevronLeft" size={18} />
            </button>
            
            <h2 className="text-2xl font-bold text-gray-900 min-w-[180px] text-center">
              {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Icon name="ChevronRight" size={18} />
            </button>
          </div>
          
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            Today
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">View:</span>
          <div className="inline-flex rounded-lg border border-gray-200 p-1">
            {[
              { id: 'month', label: 'Month', icon: 'Calendar' },
              { id: 'week', label: 'Week', icon: 'CalendarDays' }
            ]?.map((mode) => (
              <button
                key={mode?.id}
                onClick={() => setViewMode(mode?.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === mode?.id
                    ? 'bg-indigo-100 text-indigo-700' :'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon name={mode?.icon} size={14} />
                {mode?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50/50 rounded-xl">
        <span className="text-sm font-medium text-gray-600">Leave Types:</span>
        {Object.entries(leaveTypes)?.map(([key, type]) => (
          <div key={key} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${type?.color}`} />
            <span className="text-sm text-gray-700">{type?.name}</span>
          </div>
        ))}
      </div>
      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Days of Week Header */}
        <div className="grid grid-cols-7 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
            <div key={day} className="px-3 py-4 text-center">
              <span className="text-sm font-semibold text-gray-700">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days?.map((day, index) => {
            const leaves = getLeaveForDate(day?.dateString);
            const isCurrentDay = isToday(day?.date);
            
            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-gray-100 p-2 hover:bg-gray-50/50 transition-colors cursor-pointer ${
                  !day?.isCurrentMonth ? 'bg-gray-25 text-gray-400' : ''
                } ${isCurrentDay ? 'bg-indigo-50 ring-2 ring-indigo-200' : ''}`}
                onClick={() => setSelectedDate(day)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${
                    isCurrentDay ? 'text-indigo-600 font-bold' : day?.isCurrentMonth ?'text-gray-900' : 'text-gray-400'
                  }`}>
                    {day?.date?.getDate()}
                  </span>
                  {leaves?.length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-indigo-600 rounded-full">
                      {leaves?.length}
                    </span>
                  )}
                </div>
                {/* Leave Items */}
                <div className="space-y-1">
                  {leaves?.slice(0, 3)?.map((leave) => {
                    const leaveType = leaveTypes?.[leave?.type];
                    return (
                      <div
                        key={leave?.id}
                        className={`text-xs px-2 py-1 rounded-md truncate ${leaveType?.bgColor} ${leaveType?.textColor}`}
                        title={`${leave?.employeeName} - ${leaveType?.name} (${leave?.status})`}
                      >
                        {leave?.employeeName}
                      </div>
                    );
                  })}
                  {leaves?.length > 3 && (
                    <div className="text-xs text-gray-500 font-medium px-2">
                      +{leaves?.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Selected Date Details Modal */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedDate?.date?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {getLeaveForDate(selectedDate?.dateString)?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Calendar" size={48} className="text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No time off scheduled for this date</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getLeaveForDate(selectedDate?.dateString)?.map((leave) => {
                    const leaveType = leaveTypes?.[leave?.type];
                    return (
                      <div key={leave?.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-4 h-4 rounded-full ${leaveType?.color}`} />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{leave?.employeeName}</h4>
                          <p className="text-sm text-gray-600">{leaveType?.name}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          leave?.status === 'approved' ? 'bg-green-100 text-green-800' :
                          leave?.status === 'pending'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {leave?.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCalendar;